'use client';
import React from 'react';
import { ExternalLink, CheckCircle, AlertCircle, Clock, Sparkles, RefreshCw } from 'lucide-react';
import type { PriceResearch } from '@/lib/claimStore';

interface PriceResearchPanelProps {
  itemName: string;
  research: PriceResearch | null;
  isLoading: boolean;
  onResearch: () => void;
}

const CONFIDENCE_CONFIG = {
  high: { label: 'High confidence', color: 'text-positive', icon: CheckCircle },
  medium: { label: 'Medium confidence', color: 'text-warning', icon: AlertCircle },
  low: { label: 'Low confidence', color: 'text-negative', icon: AlertCircle },
};

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function truncateUrl(url: string, max = 50) {
  try {
    const u = new URL(url);
    const display = u.hostname + u.pathname;
    return display.length > max ? display.slice(0, max) + '…' : display;
  } catch {
    return url.length > max ? url.slice(0, max) + '…' : url;
  }
}

export default function PriceResearchPanel({
  itemName,
  research,
  isLoading,
  onResearch,
}: PriceResearchPanelProps) {
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary/30">
        <div className="flex items-center gap-2">
          <Sparkles size={15} className="text-primary" />
          <h3 className="text-sm font-semibold text-foreground">AI Price Research</h3>
        </div>
        <span className="text-xs text-muted-foreground">Powered by Perplexity</span>
      </div>

      <div className="p-4">
        {/* No item name yet */}
        {!itemName && !research && !isLoading && (
          <div className="text-center py-6">
            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center mx-auto mb-3">
              <Sparkles size={18} className="text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">Enter an item name above, then click Research to find the current replacement price.</p>
          </div>
        )}

        {/* Loading skeleton */}
        {isLoading && (
          <div className="space-y-3 animate-pulse">
            <div className="h-8 bg-muted rounded-lg w-1/2" />
            <div className="h-4 bg-muted rounded w-3/4" />
            <div className="h-4 bg-muted rounded w-2/3" />
            <div className="h-3 bg-muted rounded w-1/2" />
            <p className="text-xs text-muted-foreground text-center pt-1 animate-none">Searching the web for current prices…</p>
          </div>
        )}

        {/* Research result */}
        {!isLoading && research && (
          <div className="space-y-4 fade-in">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Replacement Price (New)</p>
                {research.price > 0 ? (
                  <p className="text-3xl font-bold text-foreground tabular-nums font-mono">
                    {formatCurrency(research.price)}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground italic">Price not found — enter manually</p>
                )}
              </div>
              <button
                type="button"
                onClick={onResearch}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-border rounded-lg bg-card hover:bg-secondary transition-all duration-150 active:scale-95 text-secondary-foreground shrink-0"
              >
                <RefreshCw size={12} /> Re-research
              </button>
            </div>

            {/* Confidence */}
            {(() => {
              const conf = CONFIDENCE_CONFIG[research.confidence];
              const ConfIcon = conf.icon;
              return (
                <div className={`flex items-center gap-1.5 text-xs font-medium ${conf.color}`}>
                  <ConfIcon size={13} />
                  {conf.label}
                </div>
              );
            })()}

            {/* Primary source */}
            {research.sourceUrl && (
              <div className="bg-secondary/50 rounded-lg p-3 space-y-1.5">
                <p className="text-xs font-semibold text-foreground">Primary Source</p>
                <p className="text-sm text-foreground font-medium">{research.sourceName}</p>
                <a
                  href={research.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-info hover:underline break-all"
                >
                  <ExternalLink size={11} className="shrink-0" />
                  {truncateUrl(research.sourceUrl)}
                </a>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock size={11} />
                  Researched {research.researchDate}
                </p>
              </div>
            )}

            {/* Additional cited sources */}
            {research.sources && research.sources.length > 1 && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-foreground">All Cited Sources</p>
                <div className="space-y-1">
                  {research.sources.map((src, idx) => (
                    <a
                      key={idx}
                      href={src.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs text-info hover:underline bg-secondary/30 rounded px-2 py-1.5"
                    >
                      <ExternalLink size={10} className="shrink-0 text-muted-foreground" />
                      <span className="truncate">{src.name || truncateUrl(src.url)}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* AI summary notes */}
            {research.notes && (
              <div className="text-xs text-muted-foreground bg-secondary/30 rounded-lg px-3 py-2 leading-relaxed">
                {research.notes}
              </div>
            )}

            <p className="text-xs text-muted-foreground border-t border-border pt-3">
              Prices are current retail replacement values (new). You may override this price manually in the field below.
            </p>
          </div>
        )}

        {/* Research button — shown when item name exists but no research yet */}
        {itemName && !research && !isLoading && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Ready to research replacement price for <strong className="text-foreground">{itemName}</strong>.
            </p>
            <button
              type="button"
              onClick={onResearch}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-amber-700 transition-all duration-150 active:scale-95"
            >
              <Sparkles size={15} />
              Research Replacement Price
            </button>
            <p className="text-xs text-muted-foreground text-center">
              Uses Perplexity AI to find current retail price with source citations
            </p>
          </div>
        )}
      </div>
    </div>
  );
}